import type { HubConnection } from '@microsoft/signalr'
import { SignalRProvider, useSignalR } from 'BrokerTsLib/src/context/SignalRProvider'
import type { OfferAddon, OfferProperty, OfferTravel, OfferVehicle } from 'BrokerTsLib/src/dtos'
import React, { useContext } from 'react'

import type { PoleasImportPreviewRow, RowImportStatus } from '@/dtos/PoleasImport'

import { AppSettings } from '../appSettings'
import { useAuth } from './AuthContext'
import { usePartner } from './PartnerContext'

const ClientActions = {
  'CalcFinished': (offer: OfferVehicle) => { },
  'AddonCalcFinished': (offer: OfferAddon) => { },
  'TravelCalcFinished': (offer: OfferTravel) => { },
  'PropertyCalcFinished': (offer: OfferProperty) => { },
  'PoleasImportPreviewRow': (importRow: PoleasImportPreviewRow) => { },
  'PoleasImportRow': (importRow: RowImportStatus) => { },
  'LogoutRequest': () => { },
}

export type CalcFinishedResponse = OfferVehicle | OfferAddon | OfferTravel | OfferProperty
export type CalcFinishedActionBase = 'CalcFinished' | 'AddonCalcFinished' | 'TravelCalcFinished' | 'PropertyCalcFinished'

export type ClientSignalRActions = keyof typeof ClientActions
type ClientSignalRActionsValues<K extends ClientSignalRActions> = typeof ClientActions[K]

const ServerActions = {
  'InvokeLogoutAll': () => { },
}

type ServerSignalRActions = keyof typeof ServerActions
type ServerSignalRActionsValues<K extends ServerSignalRActions> = typeof ServerActions[K]

interface ICustomSignalRContext {
  connection: HubConnection | null,
  register: <K extends ClientSignalRActions>(key: K, func: ClientSignalRActionsValues<K>) => void,
  unregister: <K extends ClientSignalRActions>(key: K) => void,
  invoke: <K extends ServerSignalRActions, T = void>(key: K, ...args: Parameters<ServerSignalRActionsValues<K>>) => Promise<T>,
}

export const CommonSignalRContext = React.createContext<ICustomSignalRContext>({
  connection: null,
  register: () => { },
  unregister: () => { },
  invoke: () => Promise.resolve(null as any),
})

type CommonSignalRProps = {
  children: React.ReactNode,
}
export const CommonSignalR = ({
  children,
}: CommonSignalRProps) => {
  const connection = useSignalR()

  return (
    <CommonSignalRContext.Provider
      value={{
        connection: connection,
        register: <K extends ClientSignalRActions>(key: K, func: ClientSignalRActionsValues<K>) => {
          connection?.on(key, func)
        },
        unregister: <K extends ClientSignalRActions>(key: K) => {
          connection?.off(key)
        },
        invoke: <K extends ServerSignalRActions, T = void>(key: K, ...args: Parameters<ServerSignalRActionsValues<K>>) =>
          connection?.invoke<T>(key, ...args) ?? Promise.reject('Connection is not established')
        ,
      }}
    >
      {children}
    </CommonSignalRContext.Provider>
  )
}

export const CommonSignalRProvider = ({
  children
}: {
  children: React.ReactNode;
}) => {
  const { getBaseUrl } = usePartner()
  const { user } = useAuth()

  return (
    <SignalRProvider url={`${getBaseUrl()}/${AppSettings.signalRHub}`} connect={!!user && user.logged}>
      <CommonSignalR>
        {children}
      </CommonSignalR>
    </SignalRProvider>
  )
}

export const useCommonSignalR = () => useContext(CommonSignalRContext)